/**
 * TechBackground component
 * 
 * Creates an animated technical background with:
 * - Floating particles that connect when close together
 * - Performance optimizations (visibility auto-pause, DPR scaling, dynamic density)
 * - Cohesive purple palette for dark/light themes
 */

import React, { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
}

export const TechBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const particlesRef = useRef<Particle[]>([]);
  const isTabVisibleRef = useRef<boolean>(true);
  const { darkMode } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    const resizeCanvas = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.scale(dpr, dpr);
      initParticles(width, height);
    };

    // Purple-centric palettes for light and dark modes
    const colors = {
      light: {
        particles: ['#7C3AED', '#6D28D9', '#C4B5FD', '#1E1B4B'],
        connections: 'rgba(124, 58, 237, 0.12)',
        glow: '#C4B5FD',
      },
      dark: {
        particles: ['#7C3AED', '#A78BFA', '#C4B5FD', '#818CF8'],
        connections: 'rgba(196, 181, 253, 0.15)',
        glow: '#7C3AED',
      },
    };

    const initParticles = (w: number, h: number) => {
      particlesRef.current = [];
      // Efficiency: Cap particle count based on screen area to keep performance at 60/120fps
      const particleCount = Math.min(65, Math.floor((w * h) / 22000));
      const colorScheme = darkMode ? colors.dark : colors.light;

      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          size: Math.random() * 2.5 + 1,
          opacity: Math.random() * 0.6 + 0.2,
          color: colorScheme.particles[Math.floor(Math.random() * colorScheme.particles.length)],
        });
      }
    };

    const drawParticle = (p: Particle) => {
      ctx.save();
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
      ctx.fillStyle = p.color;
      ctx.globalAlpha = p.opacity;
      ctx.fill();
      ctx.restore();
    };

    const drawConnections = () => {
      const maxDistance = 110;
      const particles = particlesRef.current;
      const connectionColor = darkMode ? colors.dark.connections : colors.light.connections;

      for (let i = 0; i < particles.length; i++) {
        const p1 = particles[i];
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p1.x - p2.x;
          const dy = p1.y - p2.y;
          const distSq = dx * dx + dy * dy;

          if (distSq < maxDistance * maxDistance) {
            const distance = Math.sqrt(distSq);
            const opacity = (1 - distance / maxDistance) * 0.4;
            ctx.save();
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = connectionColor;
            ctx.globalAlpha = opacity;
            ctx.lineWidth = 0.8;
            ctx.stroke();
            ctx.restore();
          }
        }
      }
    };

    const updateParticles = (w: number, h: number) => {
      particlesRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;

        if (p.x > w) p.x = 0;
        if (p.x < 0) p.x = w;
        if (p.y > h) p.y = 0;
        if (p.y < 0) p.y = h;
      });
    };

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const animate = () => {
      if (!isTabVisibleRef.current) return;
      const w = window.innerWidth;
      const h = window.innerHeight;

      ctx.clearRect(0, 0, w, h);

      if (!prefersReduced) {
        updateParticles(w, h);
      }
      drawConnections();
      particlesRef.current.forEach(drawParticle);

      animationRef.current = requestAnimationFrame(animate);
    };

    // Efficiency: Pause animation when browser tab is inactive/hidden
    const handleVisibilityChange = () => {
      isTabVisibleRef.current = !document.hidden;
      if (isTabVisibleRef.current) {
        animate();
      } else if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [darkMode]);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 w-full h-full pointer-events-none z-0"
        aria-hidden="true"
      />

      {/* Ambient purple gradient lighting across background */}
      <div className="fixed inset-0 pointer-events-none z-[1]" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-brand-purple/5 dark:bg-brand-purple/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-brand-light/5 dark:bg-brand-blue/10 rounded-full blur-[140px]" />
      </div>
    </>
  );
};

export default TechBackground;

